/**
 * ExerciseLibrary — Bibliothèque d'exercices Body-Up V2.
 *
 * Utilisée à 2 endroits :
 *   - Client : sous-onglet "Bibliothèque" dans le Hub Entraînements (lecture seule)
 *   - Admin  : sous-onglet "Bibliothèque" dans Programmes Pro (avec régénération d'image)
 *
 * Recherche autocomplete à partir de 2 lettres + filtres (groupe / matériel / niveau / type).
 * Aperçu grille avec image générée par Nano Banana + modal détail au clic.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search, X, Loader2, Dumbbell, Zap, ChevronDown, RefreshCw, Info, Sparkles,
  Filter, ArrowLeft, ImageOff, Wand2, Clapperboard, ThumbsUp, ThumbsDown,
  Heart, AlertTriangle, CheckSquare, Square, ListVideo, PlusCircle, Youtube, Image as ImageIcon, Play, FileText, Activity, Download, UploadCloud, MoreHorizontal, SlidersHorizontal, History, Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../../../components/ui/switch';
import { ExerciseAdminTools } from './ExerciseAdminTools';
import { NewExerciseModal } from './NewExerciseModal';
import {
  isRecentAiExercise, isRecentVideo, NewBadge, ExerciseMedia, LevelBadge, TypeIcon,
  FilterChip, MuscleGroupChip, ExerciseDetailModal, SearchBox, ReviewStatusBadge, VerifiedBadge,
  LlmBudgetWidget, AuditHistoryModal,
} from './ExerciseLibraryUI';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../../../components/ui/dropdown-menu';

const BATCH_VIDEO_MAX = 20; // même garde-fou que côté backend (coût/temps Sora 2)

// --- Composant principal -----------------------------------------------------
export default function ExerciseLibrary({ authFetch, en = false, isAdmin = false, onBack }) {
  const L = useCallback((fr, eng) => (en ? eng : fr), [en]);
  const [meta, setMeta] = useState({ muscle_groups: [], equipments: [], levels: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', muscle_group: '', equipment: '', level: '' });
  const [openId, setOpenId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(60);
  const [videoStates, setVideoStates] = useState({}); // { [exerciseId]: { status } }
  const videoPollRef = useRef({});
  const backfillPollRef = useRef(null);
  const textsPollRef = useRef(null);
  const storyboardPollRef = useRef(null);

  // Chargement méta et items
  useEffect(() => {
    authFetch('/api/exercises/meta').then((r) => r.json()).then(setMeta).catch(() => {});
  }, [authFetch]);

  // Solde LLM estimé (admin) + historique des audits de posture
  const [llmBudget, setLlmBudget] = useState(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingAuditHistory, setLoadingAuditHistory] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    authFetch('/api/exercises/admin/llm-budget').then((r) => r.json()).then(setLlmBudget).catch(() => {});
  }, [authFetch, isAdmin]);
  const openAuditHistory = useCallback(async () => {
    setShowAuditHistory(true);
    setLoadingAuditHistory(true);
    try {
      const r = await authFetch('/api/exercises/admin/audit-postures/history');
      const d = await r.json();
      setAuditHistory(d.history || []);
    } catch { setAuditHistory([]); }
    setLoadingAuditHistory(false);
  }, [authFetch]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await authFetch(`/api/exercises?${params}`);
      const data = await res.json();
      setItems(data.exercises || []);
      setVisibleCount(60); // reset pagination on filter change
    } catch { setItems([]); }
    setLoading(false);
  }, [authFetch, filters]);
  useEffect(() => { reload(); }, [reload]);

  const activeExercise = useMemo(
    () => items.find((e) => e.id === openId) || null,
    [items, openId]
  );

  const openById = useCallback(async (id) => {
    // Trouve dans la liste courante OU fetch spécifique
    const inList = items.find((e) => e.id === id);
    if (inList) { setOpenId(id); return; }
    try {
      const res = await authFetch(`/api/exercises/${id}`);
      const ex = await res.json();
      setItems((prev) => [ex, ...prev]);
      setOpenId(id);
    } catch { toast.error(L('Exercice introuvable', 'Exercise not found')); }
  }, [authFetch, items, L]);

  const updateExerciseLocal = useCallback((id, patch) => {
    setItems((prev) => prev.map((e) => e.id === id ? { ...e, ...patch } : e));
  }, []);

  const deleteExercise = useCallback(async (id) => {
    try {
      const res = await authFetch(`/api/exercises/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('failed');
      setItems((prev) => prev.filter((e) => e.id !== id));
      setOpenId(null);
      toast.success(L('Exercice supprimé', 'Exercise deleted'));
    } catch { toast.error(L('Échec de la suppression', 'Delete failed')); }
  }, [authFetch, L]);

  useEffect(() => () => {
    Object.values(videoPollRef.current).forEach(clearInterval);
    clearInterval(backfillPollRef.current);
    clearInterval(textsPollRef.current);
  }, []);

  const pollVideoStatus = useCallback((id) => {
    clearInterval(videoPollRef.current[id]);
    videoPollRef.current[id] = setInterval(async () => {
      try {
        const res = await authFetch(`/api/exercises/${id}/video-status`);
        const data = await res.json();
        if (data.video_status === 'ready') {
          clearInterval(videoPollRef.current[id]);
          setVideoStates((prev) => ({ ...prev, [id]: { status: 'ready' } }));
          setItems((prev) => prev.map((e) => e.id === id ? { ...e, video_url: data.video_url, video_status: 'ready' } : e));
        } else if (data.video_status === 'failed') {
          clearInterval(videoPollRef.current[id]);
          setVideoStates((prev) => ({ ...prev, [id]: { status: 'failed', failReason: data.video_fail_reason, failMessage: data.video_fail_message } }));
        } else if (data.video_status === 'pending') {
          // Un job en cours ('queued'/'generating') qui redevient 'pending' signifie que le
          // backend a réinitialisé une file orpheline (ex: redémarrage) — job interrompu, pas un succès.
          clearInterval(videoPollRef.current[id]);
          setVideoStates((prev) => (
            prev[id] && ['queued', 'generating'].includes(prev[id].status)
              ? { ...prev, [id]: { status: 'failed', interrupted: true } }
              : prev
          ));
        } else {
          setVideoStates((prev) => ({ ...prev, [id]: { status: data.video_status } })); // 'queued' | 'generating'
        }
      } catch { /* on réessaie au prochain tick */ }
    }, 6000);
  }, [authFetch]);

  const generateVideo = useCallback(async (id, feedback = '') => {
    setVideoStates((prev) => ({ ...prev, [id]: { status: 'generating' } }));
    try {
      const res = await authFetch(`/api/exercises/${id}/regenerate-video`, {
        method: 'POST',
        body: JSON.stringify({ feedback: feedback || '' }),
      });
      if (!res.ok) throw new Error('failed');
    } catch {
      setVideoStates((prev) => ({ ...prev, [id]: { status: 'failed' } }));
      toast.error(L('Échec du lancement de la génération vidéo', 'Failed to start video generation'));
      return;
    }
    toast.info(L('Génération vidéo Sora 2 lancée (2-5 min)…', 'Sora 2 video generation started (2-5 min)…'));
    pollVideoStatus(id);
  }, [authFetch, L, pollVideoStatus]);

  const deleteVideo = useCallback(async (id) => {
    try {
      const res = await authFetch(`/api/exercises/${id}/video`, { method: 'DELETE' });
      if (!res.ok) throw new Error('failed');
      setVideoStates((prev) => { const next = { ...prev }; delete next[id]; return next; });
      setItems((prev) => prev.map((e) => e.id === id ? { ...e, video_status: 'pending', video_url: null } : e));
      toast.success(L('Vidéo supprimée, l\'image statique est conservée', 'Video deleted, static image kept'));
    } catch {
      toast.error(L('Échec de la suppression de la vidéo', 'Failed to delete video'));
    }
  }, [authFetch, L]);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showNewExerciseModal, setShowNewExerciseModal] = useState(false);
  const [attachingYoutube, setAttachingYoutube] = useState(false);
  const [noVideoOnly, setNoVideoOnly] = useState(false);
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const attachYoutubeBatch = useCallback(async () => {
    setAttachingYoutube(true);
    try {
      const res = await authFetch('/api/exercises/batch-attach-youtube', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast.success(L(
        `${data.attached} vidéo(s) attachée(s) — ${data.remaining} exercice(s) restant(s) sans vidéo.`,
        `${data.attached} video(s) attached — ${data.remaining} exercise(s) still without a video.`
      ));
      reload();
    } catch { toast.error(L('Échec de l\'attachement en lot', 'Batch attach failed')); }
    finally { setAttachingYoutube(false); }
  }, [authFetch, L, reload]);

  const [backfillingImages, setBackfillingImages] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const exportLibraryCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const qs = new URLSearchParams();
      if (filters.muscle_group) qs.set('muscle_group', filters.muscle_group);
      if (noVideoOnly) qs.set('video_status', 'no_video');
      const res = await authFetch(`/api/exercises/admin/export.csv${qs.toString() ? `?${qs.toString()}` : ''}`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'body-up-exercise-library.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(L('Export CSV téléchargé (filtres actifs appliqués).', 'CSV export downloaded (active filters applied).'));
    } catch {
      toast.error(L("Échec de l'export CSV.", 'CSV export failed.'));
    } finally {
      setExportingCsv(false);
    }
  }, [authFetch, L, filters.muscle_group, noVideoOnly]);
  const backfillImages = useCallback(async () => {
    setBackfillingImages(true);
    try {
      const res = await authFetch('/api/exercises/backfill-images', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (data.queued === 0) {
        toast.success(L('Toutes les images sont déjà générées.', 'All images are already generated.'));
        setBackfillingImages(false);
        return;
      }
      toast.info(L(`Génération de ${data.queued} image(s) manquante(s) lancée en arrière-plan…`,
                    `Generating ${data.queued} missing image(s) in the background…`));
      clearInterval(backfillPollRef.current);
      backfillPollRef.current = setInterval(async () => {
        try {
          const r = await authFetch('/api/exercises/admin/seed-status');
          const s = await r.json();
          const readyCount = s.counts?.ready || 0;
          if (s.total > 0 && readyCount >= s.total) {
            clearInterval(backfillPollRef.current);
            setBackfillingImages(false);
            toast.success(L('Toutes les images de la bibliothèque sont prêtes !', 'All library images are ready!'));
            reload();
          }
        } catch { /* on réessaie au prochain tick */ }
      }, 8000);
    } catch {
      toast.error(L('Échec du lancement de la génération d\'images', 'Failed to start image generation'));
      setBackfillingImages(false);
    }
  }, [authFetch, L, reload]);

  const [backfillingStoryboards, setBackfillingStoryboards] = useState(false);
  const backfillStoryboards = useCallback(async () => {
    setBackfillingStoryboards(true);
    try {
      const res = await authFetch('/api/exercises/admin/generate-missing-storyboards', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (data.queued === 0) {
        toast.success(L('Tous les exercices ont déjà leur storyboard musculaire.', 'All exercises already have their muscle storyboard.'));
        setBackfillingStoryboards(false);
        return;
      }
      toast.info(L(`Génération de ${data.queued} storyboard(s) manquant(s) lancée en arrière-plan (peut prendre plusieurs heures)…`,
                    `Generating ${data.queued} missing storyboard(s) in the background (may take several hours)…`));
      clearInterval(storyboardPollRef.current);
      storyboardPollRef.current = setInterval(async () => {
        try {
          const r = await authFetch('/api/exercises/admin/seed-status');
          const s = await r.json();
          const storyboardCount = s.image_mode_counts?.muscle_storyboard || 0;
          if (s.total > 0 && storyboardCount >= s.total) {
            clearInterval(storyboardPollRef.current);
            setBackfillingStoryboards(false);
            toast.success(L('Tous les storyboards musculaires sont prêts !', 'All muscle storyboards are ready!'));
            reload();
          }
        } catch { /* on réessaie au prochain tick */ }
      }, 15000);
    } catch {
      toast.error(L('Échec du lancement de la génération de storyboards', 'Failed to start storyboard generation'));
      setBackfillingStoryboards(false);
    }
  }, [authFetch, L, reload]);

  const [generatingTexts, setGeneratingTexts] = useState(false);
  const [textsRemaining, setTextsRemaining] = useState(null);
  const generateMissingTexts = useCallback(async () => {
    if (!window.confirm(L(
      'Lancer la génération IA (Claude) des textes d\'exécution + conseils pro pour tous les exercices qui n\'en ont pas encore ? Cette action peut prendre plusieurs minutes.',
      'Launch AI generation (Claude) of execution texts + pro tips for all exercises that don\'t have one yet? This may take several minutes.'
    ))) return;
    setGeneratingTexts(true);
    try {
      const res = await authFetch('/api/exercises/admin/generate-missing-texts', { method: 'POST' });
      if (res.status === 409) {
        toast.info(L('Une génération de textes est déjà en cours (reprise auto possible après un redémarrage serveur).', 'A text generation is already in progress (auto-resumes after a server restart).'));
      } else if (res.ok) {
        const data = await res.json();
        if (data.queued === 0) {
          toast.success(L('Tous les exercices ont déjà un texte d\'exécution.', 'All exercises already have an execution text.'));
          setGeneratingTexts(false);
          return;
        }
        setTextsRemaining(data.queued);
        toast.info(L(`Génération de ${data.queued} texte(s) manquant(s) lancée en arrière-plan…`,
                      `Generating ${data.queued} missing text(s) in the background…`));
      } else {
        throw new Error('failed');
      }
      // Poll toujours démarré même sur 409 (déjà en cours) — affiche la progression réelle
      // quelle que soit l'origine du lancement (ce bouton, un autre onglet, ou l'auto-reprise
      // serveur au boot).
      clearInterval(textsPollRef.current);
      textsPollRef.current = setInterval(async () => {
        try {
          const r = await authFetch('/api/exercises/admin/seed-status');
          const s = await r.json();
          setTextsRemaining(s.texts_missing || 0);
          if ((s.texts_missing || 0) === 0) {
            clearInterval(textsPollRef.current);
            setGeneratingTexts(false);
            setTextsRemaining(null);
            toast.success(L('Tous les textes d\'exécution sont générés !', 'All execution texts are generated!'));
            reload();
          }
        } catch { /* on réessaie au prochain tick */ }
      }, 8000);
    } catch {
      toast.error(L('Échec du lancement de la génération de textes', 'Failed to start text generation'));
      setGeneratingTexts(false);
    }
  }, [authFetch, L, reload]);

  // Audit IA (vision Gemini) de la posture de tous les exercices déjà illustrés — repère les
  // images dont la posture ne colle pas au texte d'exécution et régénère automatiquement
  // celles détectées en écart.
  const [auditingPostures, setAuditingPostures] = useState(false);
  const [postureAuditProgress, setPostureAuditProgress] = useState(null); // { checked, total }
  const postureAuditPollRef = useRef(null);
  const auditPostures = useCallback(async () => {
    if (!window.confirm(L(
      'Lancer un audit IA (vision Gemini) de la posture sur TOUS les exercices déjà illustrés, puis régénérer automatiquement les images en écart ? Peut prendre plusieurs dizaines de minutes.',
      'Launch an AI audit (Gemini vision) of posture on ALL already-illustrated exercises, then auto-regenerate images found mismatched? May take several dozen minutes.'
    ))) return;
    setAuditingPostures(true);
    try {
      const res = await authFetch('/api/exercises/admin/audit-postures', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (data.queued === 0) {
        toast.success(L('Aucune image à auditer.', 'No image to audit.'));
        setAuditingPostures(false);
        return;
      }
      setPostureAuditProgress({ checked: 0, total: data.queued });
      toast.info(L(`Audit de posture lancé sur ${data.queued} exercice(s) en arrière-plan…`,
                    `Posture audit started on ${data.queued} exercise(s) in the background…`));
      clearInterval(postureAuditPollRef.current);
      postureAuditPollRef.current = setInterval(async () => {
        try {
          const r = await authFetch('/api/exercises/admin/audit-postures/status');
          const s = await r.json();
          const checkedCount = Object.entries(s.counts || {})
            .filter(([k]) => k !== 'checking').reduce((sum, [, v]) => sum + v, 0);
          setPostureAuditProgress({ checked: checkedCount, total: data.queued });
          if (!s.running) {
            clearInterval(postureAuditPollRef.current);
            setAuditingPostures(false);
            setPostureAuditProgress(null);
            const mismatches = s.counts?.mismatch || 0;
            const regenerated = s.counts?.regenerated || 0;
            toast.success(L(
              `Audit terminé : ${mismatches + regenerated} écart(s) détecté(s), ${regenerated} image(s) régénérée(s).`,
              `Audit complete: ${mismatches + regenerated} mismatch(es) found, ${regenerated} image(s) regenerated.`
            ));
            reload();
          }
        } catch { /* on réessaie au prochain tick */ }
      }, 10000);
    } catch {
      toast.error(L('Échec du lancement de l\'audit de posture', 'Failed to start posture audit'));
      setAuditingPostures(false);
    }
  }, [authFetch, L, reload]);

  // Bouton "Synchroniser depuis Preview" — à utiliser en PRODUCTION uniquement : récupère les
  // médias déjà générés sur l'environnement source configuré (MEDIA_SYNC_SOURCE_URL côté
  // backend) sans re-consommer de crédits IA. Sans effet si non configuré (voir backend/.env).
  const [syncingFromSource, setSyncingFromSource] = useState(false);
  const syncFromSource = useCallback(async () => {
    setSyncingFromSource(true);
    try {
      const res = await authFetch('/api/exercises/admin/sync-from-source', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      toast.success(L(
        `Synchronisation terminée : ${data.updated} exercice(s) mis à jour, ${data.skipped} déjà à jour/ignoré(s).`,
        `Sync complete: ${data.updated} exercise(s) updated, ${data.skipped} already up to date/skipped.`
      ));
      reload();
    } catch (e) {
      toast.error(L(`Échec de la synchronisation : ${e.message}`, `Sync failed: ${e.message}`));
    } finally { setSyncingFromSource(false); }
  }, [authFetch, L, reload]);

  // Bouton "Envoyer vers Production" — à utiliser en PREVIEW : pousse les médias/textes déjà
  // générés ici vers FRONTEND_URL (URL production stable, ne change jamais entre forks —
  // contrairement à MEDIA_SYNC_SOURCE_URL côté prod qui doit être remise à jour à chaque
  // nouveau fork preview, cause du bug "la synchro ne met pas les textes").
  const [pushingToProd, setPushingToProd] = useState(false);
  const pushToProduction = useCallback(async () => {
    setPushingToProd(true);
    try {
      const res = await authFetch('/api/exercises/admin/push-to-production', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      toast.success(L(
        `Envoi terminé : ${data.updated} exercice(s) mis à jour sur production, ${data.skipped} déjà à jour/ignoré(s).`,
        `Push complete: ${data.updated} exercise(s) updated on production, ${data.skipped} already up to date/skipped.`
      ));
    } catch (e) {
      toast.error(L(`Échec de l'envoi vers production : ${e.message}`, `Push to production failed: ${e.message}`));
    } finally { setPushingToProd(false); }
  }, [authFetch, L]);

  // Jauge globale Sora 2 (admin) — indépendante des lots ponctuels, reflète l'état RÉEL de
  // toute la bibliothèque (utile pendant un "Lancer tout" qui dure des heures).
  const [videoGauge, setVideoGauge] = useState(null); // { ready, total }
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await authFetch('/api/exercises/admin/seed-status');
        const s = await r.json();
        if (!cancelled) setVideoGauge({ ready: s.video_counts?.ready || 0, total: s.total || 0 });
      } catch { /* on réessaie au prochain tick */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin, authFetch]);

  // Switch global "vidéos visibles pour les clients" — pendant que la bibliothèque vidéo est
  // finalisée, désactivé par défaut = la bibliothèque reste en mode image (storyboard) pour
  // tous les clients, quel que soit l'avancement réel de la génération Sora 2.
  const [videoVisibility, setVideoVisibilityState] = useState(null); // { enabled }
  const [togglingVideoVisibility, setTogglingVideoVisibility] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    authFetch('/api/exercises/admin/video-visibility').then((r) => r.json()).then(setVideoVisibilityState).catch(() => {});
  }, [isAdmin, authFetch]);
  const toggleGlobalVideoVisibility = useCallback(async () => {
    const next = !videoVisibility?.enabled;
    setTogglingVideoVisibility(true);
    try {
      const res = await authFetch('/api/exercises/admin/video-visibility', { method: 'POST', body: JSON.stringify({ enabled: next }) });
      if (!res.ok) throw new Error('failed');
      setVideoVisibilityState({ enabled: next });
      toast.success(next
        ? L('Vidéos activées pour tous les clients !', 'Videos enabled for all clients!')
        : L('Vidéos masquées pour les clients (mode image storyboard uniquement).', 'Videos hidden from clients (storyboard image mode only).'));
    } catch { toast.error(L('Échec du changement de visibilité', 'Failed to change visibility')); }
    finally { setTogglingVideoVisibility(false); }
  }, [authFetch, videoVisibility, L]);

  const [batchProgress, setBatchProgress] = useState(null); // { ids: [], total }

  const launchBatchVideo = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setVideoStates((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = { status: 'queued' }; });
      return next;
    });
    let data;
    try {
      const res = await authFetch('/api/exercises/batch-regenerate-video', {
        method: 'POST', body: JSON.stringify({ exercise_ids: ids }),
      });
      if (!res.ok) throw new Error('failed');
      data = await res.json();
    } catch {
      toast.error(L('Échec du lancement de la file Sora 2', 'Failed to start the Sora 2 queue'));
      setVideoStates((prev) => {
        const next = { ...prev };
        ids.forEach((id) => { delete next[id]; });
        return next;
      });
      return;
    }
    const skippedIds = data.skipped_ids || [];
    const queuedIds = ids.filter((id) => !skippedIds.includes(id));
    if (skippedIds.length) {
      setVideoStates((prev) => {
        const next = { ...prev };
        skippedIds.forEach((id) => { delete next[id]; });
        return next;
      });
      toast.warning(L(
        `${skippedIds.length} exercice(s) ignoré(s) — image pas encore générée.`,
        `${skippedIds.length} exercise(s) skipped — no image generated yet.`
      ));
    }
    setSelectedIds([]);
    setSelectMode(false);
    if (queuedIds.length === 0) return;
    toast.info(L(`File Sora 2 lancée pour ${queuedIds.length} exercice(s) (traités un par un, 2-5 min chacun)…`,
                  `Sora 2 queue started for ${queuedIds.length} exercise(s) (processed one by one, 2-5 min each)…`));
    queuedIds.forEach((id) => pollVideoStatus(id));
    setBatchProgress({ ids: queuedIds, total: queuedIds.length });
  }, [authFetch, selectedIds, L, pollVideoStatus]);

  // Progression globale de la file Sora 2 en lot — se termine quand tous les exercices
  // de la file ont atteint un statut final (ready ou failed).
  const batchDoneCount = useMemo(() => {
    if (!batchProgress) return 0;
    return batchProgress.ids.filter((id) => ['ready', 'failed'].includes(videoStates[id]?.status)).length;
  }, [batchProgress, videoStates]);

  useEffect(() => {
    if (!batchProgress) return;
    if (batchDoneCount < batchProgress.total) return;
    const failedCount = batchProgress.ids.filter((id) => videoStates[id]?.status === 'failed').length;
    const interruptedCount = batchProgress.ids.filter((id) => videoStates[id]?.interrupted).length;
    const successCount = batchProgress.total - failedCount;
    if (interruptedCount > 0) {
      toast.warning(L(
        `File Sora 2 interrompue (redémarrage serveur) : ${successCount}/${batchProgress.total} réussie(s), ${interruptedCount} interrompue(s) — relancez-les si besoin.`,
        `Sora 2 queue interrupted (server restart): ${successCount}/${batchProgress.total} succeeded, ${interruptedCount} interrupted — relaunch if needed.`
      ));
    } else {
      toast.success(L(
        `File Sora 2 terminée : ${successCount}/${batchProgress.total} réussie(s)${failedCount ? `, ${failedCount} échec(s)` : ''}.`,
        `Sora 2 queue done: ${successCount}/${batchProgress.total} succeeded${failedCount ? `, ${failedCount} failed` : ''}.`
      ));
    }
    setBatchProgress(null);
  }, [batchDoneCount, batchProgress, videoStates, L]);

  const setFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: prev[k] === v ? '' : v }));
  // "Cardio" comme groupe musculaire n'existe QUE sur les exercices de type cardio (aucun
  // exercice musculation n'a ce groupe) — sans ce garde-fou, sélectionner Musculation + Cardio
  // (groupe) donne silencieusement 0 résultat sans que l'utilisateur comprenne pourquoi.
  const setTypeFilter = (v) => setFilters((prev) => {
    const nextType = prev.type === v ? '' : v;
    const clearsCardioGroup = nextType === 'musculation' && prev.muscle_group === 'Cardio';
    return { ...prev, type: nextType, muscle_group: clearsCardioGroup ? '' : prev.muscle_group };
  });
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [badRatedOnly, setBadRatedOnly] = useState(false);
  const [noTextOnly, setNoTextOnly] = useState(false);
  const [recentVideoOnly, setRecentVideoOnly] = useState(false);
  const [postureMissingOnly, setPostureMissingOnly] = useState(false);
  const [postureMismatchOnly, setPostureMismatchOnly] = useState(false);
  const [clientAiOnly, setClientAiOnly] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const badRatingThreshold = meta.bad_rating_threshold || 2;

  const displayedItems = useMemo(() => {
    let list = items;
    if (favoritesOnly) list = list.filter((e) => e.my_rating === 1);
    if (badRatedOnly) list = list.filter((e) => ((e.dislikes_count || 0) - (e.likes_count || 0)) >= badRatingThreshold);
    if (noVideoOnly) list = list.filter((e) => e.video_status !== 'ready');
    if (noTextOnly) list = list.filter((e) => !(e.execution || '').trim());
    if (recentVideoOnly) list = list.filter(isRecentVideo);
    if (postureMissingOnly) list = list.filter((e) => e.posture_missing);
    if (postureMismatchOnly) list = list.filter((e) => e.posture_audit_status === 'mismatch');
    if (clientAiOnly) list = list.filter((e) => e.client_ai_generated);
    return list;
  }, [items, favoritesOnly, badRatedOnly, noVideoOnly, noTextOnly, recentVideoOnly, postureMissingOnly, postureMismatchOnly, clientAiOnly, badRatingThreshold]);
  const recentVideoCount = useMemo(() => items.filter(isRecentVideo).length, [items]);
  const noVideoCount = useMemo(() => items.filter((e) => e.video_status !== 'ready').length, [items]);
  const noTextCount = useMemo(() => items.filter((e) => !(e.execution || '').trim()).length, [items]);
  const postureMissingCount = useMemo(() => items.filter((e) => e.posture_missing).length, [items]);
  const postureMismatchCount = useMemo(() => items.filter((e) => e.posture_audit_status === 'mismatch').length, [items]);
  const clientAiCount = useMemo(() => items.filter((e) => e.client_ai_generated).length, [items]);
  const selectAllForVideo = useCallback(() => {
    setSelectedIds(displayedItems.filter((e) => e.video_status !== 'ready' && e.image_status === 'ready').slice(0, BATCH_VIDEO_MAX).map((e) => e.id));
  }, [displayedItems]);
  const [launchingAllVideos, setLaunchingAllVideos] = useState(false);
  const launchAllVideos = useCallback(async () => {
    if (noVideoCount === 0) { toast.success(L('Toute la bibliothèque a déjà sa vidéo Sora 2 !', 'The whole library already has its Sora 2 video!')); return; }
    const confirmed = window.confirm(L(
      `Lancer Sora 2 sur les ${noVideoCount} exercice(s) restant(s) ? Traitement séquentiel en arrière-plan côté serveur, environ ${Math.ceil(noVideoCount * 3 / 60)}h estimées (2-5 min/vidéo). Vous pouvez fermer cette page, ça continue côté serveur.`,
      `Launch Sora 2 on the remaining ${noVideoCount} exercise(s)? Sequential background processing on the server, roughly ${Math.ceil(noVideoCount * 3 / 60)}h estimated (2-5 min/video). You can close this page, it keeps running server-side.`
    ));
    if (!confirmed) return;
    setLaunchingAllVideos(true);
    try {
      const res = await authFetch('/api/exercises/regenerate-video-all', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast.info(L(`File Sora 2 lancée pour ${data.queued} exercice(s) — ça tourne en arrière-plan sur le serveur.`,
                    `Sora 2 queue started for ${data.queued} exercise(s) — running in the background on the server.`));
      if (data.skipped_no_image > 0) {
        toast.warning(L(
          `${data.skipped_no_image} exercice(s) ignoré(s) — image pas encore générée.`,
          `${data.skipped_no_image} exercise(s) skipped — no image generated yet.`
        ));
      }
    } catch { toast.error(L('Échec du lancement', 'Failed to launch')); }
    finally { setLaunchingAllVideos(false); }
  }, [authFetch, L, noVideoCount]);
  const badRatedCount = useMemo(
    () => items.filter((e) => ((e.dislikes_count || 0) - (e.likes_count || 0)) >= badRatingThreshold).length,
    [items, badRatingThreshold]
  );
  const newThisWeekCount = useMemo(() => items.filter(isRecentAiExercise).length, [items]);

  const rateExercise = useCallback(async (id, rating) => {
    const current = items.find((e) => e.id === id);
    const alreadyVoted = current?.my_rating === rating;
    const prevSnapshot = current ? { likes_count: current.likes_count, dislikes_count: current.dislikes_count, my_rating: current.my_rating } : null;

    // Mise à jour optimiste immédiate (ressenti instantané), corrigée/annulée selon la réponse serveur.
    setItems((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      let likes = e.likes_count || 0, dislikes = e.dislikes_count || 0;
      if (e.my_rating === 1) likes -= 1;
      if (e.my_rating === -1) dislikes -= 1;
      if (!alreadyVoted) {
        if (rating === 1) likes += 1; else dislikes += 1;
      }
      return { ...e, likes_count: likes, dislikes_count: dislikes, my_rating: alreadyVoted ? null : rating };
    }));

    try {
      const res = alreadyVoted
        ? await authFetch(`/api/exercises/${id}/rate`, { method: 'DELETE' })
        : await authFetch(`/api/exercises/${id}/rate`, { method: 'POST', body: JSON.stringify({ rating }) });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setItems((prev) => prev.map((e) => e.id === id
        ? { ...e, likes_count: data.likes_count, dislikes_count: data.dislikes_count, my_rating: data.my_rating }
        : e));
    } catch {
      if (prevSnapshot) setItems((prev) => prev.map((e) => e.id === id ? { ...e, ...prevSnapshot } : e));
      toast.error(L('Échec de l\'envoi de votre avis', 'Failed to send your feedback'));
    }
  }, [authFetch, items, L]);

  return (
    <div className="space-y-4" data-testid="exercise-library">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {onBack && (
            <button onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="library-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-primary" />
            {L('Bibliothèque d\'exercices', 'Exercise library')}
          </h2>
          <span className="text-xs text-zinc-500 font-bold" data-testid="library-count">({displayedItems.length})</span>
          {isAdmin && newThisWeekCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              data-testid="new-this-week-badge">
              <Sparkles className="w-2.5 h-2.5" /> {L(`${newThisWeekCount} nouveau(x) cette semaine`, `${newThisWeekCount} new this week`)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <LlmBudgetWidget en={en} budget={llmBudget} authFetch={authFetch} onUpdated={setLlmBudget} />
          )}
          {isAdmin && (
            <button
              onClick={() => setShowNewExerciseModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-emerald-500/40"
              data-testid="new-exercise-btn"
            >
              <PlusCircle className="w-3.5 h-3.5" /> {L('Nouvel exercice', 'New exercise')}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => { setSelectMode((v) => !v); setSelectedIds([]); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                selectMode ? 'bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-500/40' : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-fuchsia-500/30'
              }`}
              data-testid="toggle-select-mode-btn"
            >
              <ListVideo className="w-3.5 h-3.5" />
              {selectMode ? L('Annuler la sélection', 'Cancel selection') : L('Sélection multiple (Sora 2)', 'Multi-select (Sora 2)')}
            </button>
          )}
          {isAdmin && selectMode && (
            <button
              onClick={selectAllForVideo}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-fuchsia-500/30"
              data-testid="select-all-novideo-btn"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              {L(`Sélectionner tout (${Math.min(BATCH_VIDEO_MAX, displayedItems.filter((e) => e.video_status !== 'ready').length)})`,
                 `Select all (${Math.min(BATCH_VIDEO_MAX, displayedItems.filter((e) => e.video_status !== 'ready').length)})`)}
            </button>
          )}
          {/* Actions avancées / lots — regroupées dans un menu discret pour éviter les clics
              accidentels (chaque action peut modifier ou publier du contenu en masse, non relu
              individuellement avant publication). */}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-primary/40"
                  data-testid="advanced-actions-menu-btn"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" /> {L('Actions avancées', 'Advanced actions')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10 text-zinc-200 w-72" data-testid="advanced-actions-menu">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">{L('Synchronisation', 'Sync')}</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={syncFromSource} disabled={syncingFromSource}
                  className="focus:bg-sky-500/10 gap-2 cursor-pointer" data-testid="sync-from-source-btn"
                >
                  {syncingFromSource ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 text-sky-400" />}
                  {L('Synchroniser depuis Preview', 'Sync from Preview')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={pushToProduction} disabled={pushingToProd}
                  className="focus:bg-fuchsia-500/10 gap-2 cursor-pointer" data-testid="push-to-production-btn"
                >
                  {pushingToProd ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 text-fuchsia-400" />}
                  {L('Envoyer vers Production', 'Push to Production')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">{L('Génération en lot (IA)', 'Batch generation (AI)')}</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={backfillImages} disabled={backfillingImages}
                  className="focus:bg-amber-500/10 gap-2 cursor-pointer" data-testid="backfill-images-btn"
                >
                  {backfillingImages ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 text-amber-400" />}
                  {L('Générer images manquantes', 'Generate missing images')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={backfillStoryboards} disabled={backfillingStoryboards}
                  className="focus:bg-red-500/10 gap-2 cursor-pointer" data-testid="backfill-storyboards-btn"
                >
                  {backfillingStoryboards ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 text-red-400" />}
                  {L('Générer storyboards musculaires manquants', 'Generate missing muscle storyboards')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={generateMissingTexts} disabled={generatingTexts}
                  className="focus:bg-emerald-500/10 gap-2 cursor-pointer" data-testid="generate-missing-texts-btn"
                >
                  {generatingTexts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-emerald-400" />}
                  {generatingTexts && textsRemaining != null
                    ? L(`Génération… (${textsRemaining} restant(s))`, `Generating… (${textsRemaining} remaining)`)
                    : L('Générer textes manquants (IA)', 'Generate missing texts (AI)')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={launchAllVideos} disabled={launchingAllVideos}
                  className="focus:bg-fuchsia-500/10 gap-2 cursor-pointer" data-testid="launch-all-videos-btn"
                >
                  {launchingAllVideos ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clapperboard className="w-3.5 h-3.5 text-fuchsia-400" />}
                  {L(`Lancer Sora 2 sur tout (${noVideoCount})`, `Launch Sora 2 on all (${noVideoCount})`)}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={auditPostures} disabled={auditingPostures}
                  className="focus:bg-orange-500/10 gap-2 cursor-pointer" data-testid="audit-postures-btn"
                >
                  {auditingPostures ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />}
                  {auditingPostures && postureAuditProgress
                    ? L(`Audit en cours… (${postureAuditProgress.checked}/${postureAuditProgress.total})`, `Auditing… (${postureAuditProgress.checked}/${postureAuditProgress.total})`)
                    : L('Auditer les postures (IA)', 'Audit postures (AI)')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={openAuditHistory}
                  className="focus:bg-orange-500/10 gap-2 cursor-pointer" data-testid="audit-history-btn"
                >
                  <History className="w-3.5 h-3.5 text-orange-400" />
                  {L('Historique des audits', 'Audit history')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={attachYoutubeBatch} disabled={attachingYoutube}
                  className="focus:bg-red-500/10 gap-2 cursor-pointer" data-testid="batch-attach-youtube-btn"
                >
                  {attachingYoutube ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Youtube className="w-3.5 h-3.5 text-red-400" />}
                  {L('Attacher YouTube (lot)', 'Attach YouTube (batch)')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onSelect={exportLibraryCsv} disabled={exportingCsv}
                  className="focus:bg-emerald-500/10 gap-2 cursor-pointer" data-testid="export-library-csv-btn"
                >
                  {exportingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 text-emerald-400" />}
                  {(filters.muscle_group || noVideoOnly) ? L('Export CSV (filtré)', 'Export CSV (filtered)') : L('Export CSV', 'Export CSV')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isAdmin && (
            <span className="text-[10px] uppercase tracking-widest text-primary font-bold px-2 py-1 rounded bg-primary/10 border border-primary/30" data-testid="admin-badge">
              Admin
            </span>
          )}
        </div>
      </div>

      {isAdmin && videoGauge && videoGauge.total > 0 && (
        <div className="flex items-center gap-3 rounded-xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] px-3 py-2 flex-wrap" data-testid="video-gauge">
          <Clapperboard className="w-4 h-4 text-fuchsia-400 flex-shrink-0" />
          <span className="text-xs font-bold text-zinc-300 whitespace-nowrap">
            {L(`Vidéos Sora 2 : ${videoGauge.ready}/${videoGauge.total}`, `Sora 2 videos: ${videoGauge.ready}/${videoGauge.total}`)}
          </span>
          <div className="flex-1 min-w-[80px] h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 transition-all duration-500"
              style={{ width: `${(videoGauge.ready / videoGauge.total) * 100}%` }} />
          </div>
          {videoVisibility && (
            <div className="flex items-center gap-2 pl-3 border-l border-white/10" data-testid="global-video-visibility-toggle">
              <span className={`text-[11px] font-bold whitespace-nowrap ${videoVisibility.enabled ? 'text-emerald-300' : 'text-zinc-500'}`}>
                {videoVisibility.enabled
                  ? L('Vidéos visibles (clients)', 'Videos visible (clients)')
                  : L('Vidéos masquées (mode image)', 'Videos hidden (image mode)')}
              </span>
              <Switch checked={!!videoVisibility.enabled} disabled={togglingVideoVisibility} onCheckedChange={toggleGlobalVideoVisibility} data-testid="global-video-visibility-switch" />
            </div>
          )}
        </div>
      )}

      <SearchBox authFetch={authFetch} en={en} onPick={openById} />

      {/* Filtres */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip active={filters.type === 'musculation'} onClick={() => setTypeFilter('musculation')} testid="filter-type-muscu">
            {L('Musculation', 'Strength')}
          </FilterChip>
          <FilterChip active={filters.type === 'cardio'} onClick={() => setTypeFilter('cardio')} testid="filter-type-cardio">
            Cardio
          </FilterChip>
          <FilterChip active={favoritesOnly} onClick={() => setFavoritesOnly((v) => !v)} testid="filter-favorites">
            <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" fill={favoritesOnly ? 'currentColor' : 'none'} /> {L('Mes favoris', 'My favorites')}</span>
          </FilterChip>
          {recentVideoCount > 0 && (
            <FilterChip active={recentVideoOnly} onClick={() => setRecentVideoOnly((v) => !v)} testid="filter-recent-video">
              <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> {L(`Vidéos récentes (${recentVideoCount})`, `Recent videos (${recentVideoCount})`)}</span>
            </FilterChip>
          )}
          <button onClick={() => setShowMoreFilters((v) => !v)}
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
              filters.equipment || filters.level ? 'bg-primary/15 text-primary border-primary/40' : 'bg-zinc-800/60 text-zinc-400 border-white/10 hover:text-white'
            }`}
            data-testid="toggle-more-filters-btn">
            <SlidersHorizontal className="w-3.5 h-3.5" /> {L('Matériel / Niveau', 'Equipment / Level')}
          </button>
          {isAdmin && badRatedCount > 0 && (
            <FilterChip active={badRatedOnly} onClick={() => setBadRatedOnly((v) => !v)} testid="filter-bad-rated">
              <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {L(`Mal notés (${badRatedCount})`, `Poorly rated (${badRatedCount})`)}</span>
            </FilterChip>
          )}
          {isAdmin && noVideoCount > 0 && (
            <FilterChip active={noVideoOnly} onClick={() => setNoVideoOnly((v) => !v)} testid="filter-no-video">
              <span className="inline-flex items-center gap-1"><Clapperboard className="w-3 h-3" /> {L(`Sans vidéo (${noVideoCount})`, `No video (${noVideoCount})`)}</span>
            </FilterChip>
          )}
          {isAdmin && noTextCount > 0 && (
            <FilterChip active={noTextOnly} onClick={() => setNoTextOnly((v) => !v)} testid="filter-no-text">
              <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {L(`Sans texte (${noTextCount})`, `No text (${noTextCount})`)}</span>
            </FilterChip>
          )}
          {isAdmin && postureMissingCount > 0 && (
            <FilterChip active={postureMissingOnly} onClick={() => setPostureMissingOnly((v) => !v)} testid="filter-posture-missing">
              <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {L(`Posture manquante (${postureMissingCount})`, `Missing posture (${postureMissingCount})`)}</span>
            </FilterChip>
          )}
          {isAdmin && postureMismatchCount > 0 && (
            <FilterChip active={postureMismatchOnly} onClick={() => setPostureMismatchOnly((v) => !v)} testid="filter-posture-mismatch">
              <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-orange-400" /> {L(`Posture erronée (${postureMismatchCount})`, `Wrong posture (${postureMismatchCount})`)}</span>
            </FilterChip>
          )}
          {isAdmin && clientAiCount > 0 && (
            <FilterChip active={clientAiOnly} onClick={() => setClientAiOnly((v) => !v)} testid="filter-client-ai-generated">
              <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3 text-cyan-400" /> {L(`Créés par IA client (sans vidéo) (${clientAiCount})`, `Client AI-created (no video) (${clientAiCount})`)}</span>
            </FilterChip>
          )}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
          {meta.muscle_groups.filter((g) => !(filters.type === 'musculation' && g === 'Cardio')).slice(0, 15).map((g) => (
            <MuscleGroupChip key={g} group={g} active={filters.muscle_group === g}
              onClick={() => setFilter('muscle_group', g)}
              testid={`filter-group-${g.toLowerCase().replace(/[^a-z]/g, '')}`} />
          ))}
        </div>

        {showMoreFilters && (
          <div className="space-y-2 p-3 rounded-xl bg-white/[0.03] border border-white/10" data-testid="more-filters-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-zinc-500 mr-1">{L('Matériel :', 'Equipment :')}</span>
              {meta.equipments.map((e) => (
                <FilterChip key={e.key} active={filters.equipment === e.key}
                  onClick={() => setFilter('equipment', e.key)}
                  testid={`filter-equipment-${e.key}`}>
                  {e.label}
                </FilterChip>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-zinc-500 mr-1">{L('Niveau :', 'Level :')}</span>
              {meta.levels.map((lv) => (
                <FilterChip key={lv.key} active={filters.level === lv.key}
                  onClick={() => setFilter('level', lv.key)}
                  testid={`filter-level-${lv.key}`}>
                  {lv.label}
                </FilterChip>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : displayedItems.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Info className="w-8 h-8 mx-auto mb-2" />
          {L('Aucun exercice ne correspond à ces filtres.', 'No exercises match these filters.')}
        </div>
      ) : (
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${selectMode ? 'pb-20' : ''}`} data-testid="exercise-grid">
          {displayedItems.slice(0, visibleCount).map((ex) => {
            const selected = selectedIds.includes(ex.id);
            const vs = videoStates[ex.id]?.status;
            return (
              <div key={ex.id} role="button" tabIndex={0}
                onClick={() => selectMode ? toggleSelect(ex.id) : setOpenId(ex.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectMode ? toggleSelect(ex.id) : setOpenId(ex.id); } }}
                className={`group relative text-left rounded-xl overflow-hidden bg-zinc-900 border transition-colors cursor-pointer ${
                  selected ? 'border-fuchsia-500' : 'border-white/10 hover:border-primary/40'
                }`}
                data-testid={`exercise-card-${ex.id}`}>
                <div className="relative aspect-square bg-zinc-950 overflow-hidden">
                  <ExerciseMedia exercise={ex} alt={ex.name_fr} testid={`exercise-card-video-${ex.id}`} autoPlayVideo={false} />
                  {selectMode && (
                    <span className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center ${selected ? 'bg-fuchsia-600 text-white' : 'bg-black/60 text-zinc-300'}`} data-testid={`select-checkbox-${ex.id}`}>
                      {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </span>
                  )}
                  {isAdmin && !selectMode && ex.video_status !== 'ready' && ex.image_status === 'ready' && vs !== 'queued' && vs !== 'generating' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); generateVideo(ex.id); }}
                      title={L('Générer une version animée (Sora 2)', 'Generate an animated version (Sora 2)')}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-zinc-300 flex items-center justify-center opacity-70 group-hover:opacity-100 hover:bg-fuchsia-600 hover:text-white transition-opacity"
                      data-testid={`quick-video-btn-${ex.id}`}>
                      <Clapperboard className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(vs === 'queued' || vs === 'generating') && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-1 text-white" data-testid={`video-progress-${ex.id}`}>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-[9px] font-bold">{vs === 'queued' ? L('En file…', 'Queued…') : L('Génération…', 'Generating…')}</span>
                    </div>
                  )}
                  {isAdmin && ex.posture_audit_status === 'mismatch' && (
                    <span title={ex.posture_audit_note || ''} className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/90 text-white text-[9px] font-bold" data-testid={`posture-mismatch-badge-${ex.id}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> {L('Posture', 'Posture')}
                    </span>
                  )}
                </div>

                <div className="p-2.5 space-y-1">
                  <p className="text-xs font-bold text-white line-clamp-2 min-h-[2.2rem]">{ex.name_fr}</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    <LevelBadge level={ex.level} en={en} />
                    <TypeIcon type={ex.type} />
                    <span className="text-[9px] text-zinc-500">{ex.muscle_group}</span>
                    {isRecentAiExercise(ex) && <NewBadge en={en} />}
                    {ex.video_status === 'ready' && <VerifiedBadge en={en} />}
                    {isAdmin && <ReviewStatusBadge exercise={ex} en={en} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {displayedItems.length > visibleCount && (
        <div className="flex justify-center py-4">
          <button onClick={() => setVisibleCount((n) => n + 60)}
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-zinc-800 border border-white/10 hover:border-primary/40"
            data-testid="library-load-more-btn">
            {L(`Voir plus (${displayedItems.length - visibleCount} restants)`, `Load more (${displayedItems.length - visibleCount} left)`)}
          </button>
        </div>
      )}

      {batchProgress && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 bg-zinc-900 border border-fuchsia-500/40 rounded-2xl shadow-2xl px-4 py-3 w-[min(92vw,340px)]" data-testid="batch-progress-bar">
          <Clapperboard className="w-4 h-4 text-fuchsia-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-white mb-1.5">
              {L(`File Sora 2 : ${batchDoneCount}/${batchProgress.total} traité(s)`, `Sora 2 queue: ${batchDoneCount}/${batchProgress.total} done`)}
            </p>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 transition-all duration-500"
                style={{ width: `${(batchDoneCount / batchProgress.total) * 100}%` }} />
            </div>
          </div>
        </div>
      )}

      {selectMode && selectedIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 bg-zinc-900 border border-fuchsia-500/40 rounded-2xl shadow-2xl px-4 py-3" data-testid="batch-action-bar">
          <span className="text-sm font-bold text-white">{L(`${selectedIds.length} sélectionné(s)`, `${selectedIds.length} selected`)}</span>
          <button onClick={launchBatchVideo}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-fuchsia-600 to-indigo-600"
            data-testid="batch-launch-video-btn">
            <Clapperboard className="w-4 h-4" />
            {L(`Lancer Sora 2 (${selectedIds.length})`, `Launch Sora 2 (${selectedIds.length})`)}
          </button>
          <button onClick={() => setSelectedIds([])} className="text-xs text-zinc-400 hover:text-white" data-testid="batch-clear-btn">
            {L('Vider', 'Clear')}
          </button>
        </div>
      )}

      <ExerciseDetailModal
        exercise={activeExercise} en={en}
        onClose={() => setOpenId(null)}
        isAdmin={isAdmin}
        onDelete={deleteExercise}
        onGenerateVideo={generateVideo}
        onDeleteVideo={deleteVideo}
        videoState={activeExercise ? videoStates[activeExercise.id] : null}
        onRate={rateExercise}
        authFetch={authFetch}
        onLocalUpdate={(patch) => activeExercise && updateExerciseLocal(activeExercise.id, patch)}
      />

      {showNewExerciseModal && (
        <NewExerciseModal
          en={en} meta={meta} authFetch={authFetch}
          onClose={() => setShowNewExerciseModal(false)}
          onCreated={(ex) => { setItems((prev) => [ex, ...prev]); setShowNewExerciseModal(false); setOpenId(ex.id); }}
        />
      )}

      <AuditHistoryModal
        en={en} open={showAuditHistory} onClose={() => setShowAuditHistory(false)}
        history={auditHistory} loading={loadingAuditHistory}
      />
    </div>
  );
}
